// HER DASHBOARD ITEM, SURFACE 2(a) — **SITE TRAFFIC AT THE VERY TOP, AND THE COLLECTOR IS THE
// MISSING ORGAN.** Her words: *visitors for the period, page views, NEW vs RETURNING, and where they
// came from (direct, Instagram, TikTok, each ad platform).*
//
// >>> MEASURED AGAINST THE CODE BEFORE A LINE WAS WRITTEN (R25/R180): THE `events` TABLE ALREADY
// RECORDS A `page_view` FOR SIGNED-OUT VISITORS AND CANNOT ANSWER ONE OF HER FOUR. <<< It carries a
// `user_id` that is NULL for every visitor who has not signed in — 6,337 of 7,480 real events on its
// own recorded measurement — so **every stranger on this site collapses into ONE bucket**. There is
// no visitor identity, so *how many visitors* and *new vs returning* have no answer; and there is no
// source column anywhere in this repo, so *where they came from* has never been recorded at all.
//
// >>> AND THE SHARPEST HALF: `funnel` IS ON `UNTRACKED_PAGES`, SO NOT ONE PAID AD CLICK HAS EVER
// BEEN RECORDED ANYWHERE ON THIS SITE. <<< She is buying TV and Instagram traffic that lands on
// `/funnel`, the ping fires there like everywhere else, and the server drops it with a 200.
//
// **THAT EXCLUSION IS CORRECT AND IS NOT LIFTED (R18: two questions, two readers).** F1 wrote the
// reason down: the rooms tile counts *which rooms her MEMBERS stand in*, and putting paid ad traffic
// into a member-behaviour report would make her busiest room the one nobody has signed up for. SITE
// TRAFFIC asks the opposite question — *who arrived at all* — so it counts the funnel, and it is its
// own ledger rather than a column bolted onto the members' one. **Both rosters are declared here and
// the funnel is on exactly one of them**, so neither exclusion can ever again be an absence somebody
// reads as coverage (R17/R160, R23/R28).
//
// It is a shared `.js` law and not a `src/lib/*.ts` for `strings.js`' own reason: **the SERVER
// resolves the source and the SERVER writes the row**, and `src/lib` is CLIENT-ONLY.
//
// >>> IT IMPORTED NOTHING UNTIL SURFACE 2(b), AND THE ONE IMPORT IT HAS NOW PRESERVES THE PROPERTY
// THAT CLAIM WAS REALLY ABOUT (R68: a declared property that no longer holds reads as a gap, so it
// is amended where it stood rather than left standing). <<< The entry requirement is that a `.mjs`
// gate can load and RUN every line of this file, and R146's own ruling is that reach is TRANSITIVE:
// `./funnels.js` imports nothing itself, so the graph is still two leaf modules and no fixture loses
// a line. It is imported rather than re-spelled because **which door a visitor landed on** is
// `funnelFromParam`'s answer and nobody else's — a second reader of that parameter would file a
// VISIT under one door while the page rendered another and the lead was filed under a third
// (R28/R29, and F1's own ruling that there is exactly one answer downstream).
// `FUNNEL_KEYS` rides in beside `funnelFromParam` — from the SAME module, so `ANALYTICS_DOORS` is
// DERIVED from her funnel roster rather than being a second copy of it, and a fourth funnel joins
// her analytics by EXISTING (R11/R31). The entry requirement is unchanged: `funnels.js` imports
// nothing, so a `.mjs` gate can still load and RUN every line of this file (R146's transitive reach).
import { funnelFromParam, FUNNEL_KEYS, funnelCopy } from "./funnels.js";
// >>> **HER `/talk` DOOR HAS ONE KEY, AND THIS IMPORT IS WHAT MAKES THAT TRUE.** <<< `landing_tag`
// has answered `ask-lala` for that door since it was declared; a traffic ledger calling the SAME
// door something else is a second answer key on the one question her report card joins on
// (R28/R29). `landingTapMap.js` imports nothing, so a `.mjs` gate still loads and RUNS every line
// of this file (R146: reach is transitive).
import { TALK_TAG } from "./landingTapMap.js";

// ---------------------------------------------------------------------------
// THE SOURCE ROSTER — HER OWN LIST, DECLARED ONCE.
//
// >>> THREE FACTS, NEVER TWO, AND THE MIDDLE ONE IS THE WHOLE ROUND (R18). <<<
//   `direct`  — NOBODY referred her. She typed the address, tapped a bookmark, or came from an app
//               that strips the referrer.
//   `other`   — SOMEBODY referred her and this house does not recognise them.
//   a name    — one of hers, below.
// **Collapsing `other` into `direct` inflates the one number that means *my own audience came back*
// with traffic she PAID for**, in the direction nobody questions (R8-4's silent one). It is its own
// key, it is on the roster, and the surface prints it.
// ---------------------------------------------------------------------------
export const TRAFFIC_SOURCES = [
  "direct",
  "instagram",
  "tiktok",
  "facebook",
  "youtube",
  "pinterest",
  "x",
  "google",
  "email",
  // THE AD PLATFORMS THIS HOUSE ACTUALLY BUYS ON, by name — VIBE-1's pixel, ROKU-1's conversions
  // API and TTV-1's tag are all live, and a campaign whose landings resolve to `other` is a
  // campaign she cannot read.
  "vibe",
  "roku",
  "trustedtv",
  // CADENCE-1 (2026-08-21): her never-paid cliffhanger cadence gets its OWN source rather than
  // folding into `email`, because her clause 5 asks to watch THIS machine earn — and a tap she
  // cannot tell from a broadcast tap is a number she cannot make a spending decision on. Additive:
  // nothing that resolved to `email` before resolves differently now.
  "cadence",
  "other",
];

const SOURCE_SET = new Set(TRAFFIC_SOURCES);
export const trafficSourceValid = (v) => typeof v === "string" && SOURCE_SET.has(v);

// The host a referrer arrives on, mapped to a source. Matched on the REGISTRABLE TAIL, so
// `l.instagram.com`, `m.facebook.com` and `www.tiktok.com` all resolve — a table of exact hosts is a
// table that misses the one spelling a platform ships next week (R11/R31's own lesson one roster up).
export const REFERRER_SOURCES = [
  ["instagram.com", "instagram"],
  ["tiktok.com", "tiktok"],
  ["facebook.com", "facebook"],
  ["fb.com", "facebook"],
  ["messenger.com", "facebook"],
  ["youtube.com", "youtube"],
  ["youtu.be", "youtube"],
  ["pinterest.com", "pinterest"],
  ["twitter.com", "x"],
  ["x.com", "x"],
  ["t.co", "x"],
  ["google.com", "google"],
  ["google.co.uk", "google"],
  ["bing.com", "google"],
  ["duckduckgo.com", "google"],
  ["mail.google.com", "email"],
  ["outlook.com", "email"],
  ["roku.com", "roku"],
  ["vibe.co", "vibe"],
  ["trustedtv.com", "trustedtv"],
];

// A UTM value is whatever an ad platform put in the link, so it is normalised and then asked of the
// roster — never trusted. An unrostered tag is `other` rather than itself, because the durable row
// may hold no free text (see `trafficSource`'s note below).
const UTM_ALIASES = new Map([
  ["ig", "instagram"],
  ["insta", "instagram"],
  ["fb", "facebook"],
  ["meta", "facebook"],
  ["yt", "youtube"],
  ["twitter", "x"],
  ["adwords", "google"],
  ["gads", "google"],
  ["newsletter", "email"],
  ["broadcast", "email"],
  ["ctv", "trustedtv"],
  ["trusted-tv", "trustedtv"],
]);

const lower = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");

// The host out of a referrer, without throwing on the rubbish a referrer really carries. `URL` is
// used rather than a regex because a referrer is a full address and its host is the only part this
// house is allowed to look at.
export function referrerHost(referrer) {
  const raw = lower(referrer);
  if (!raw) return "";
  try {
    return new URL(raw).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

// >>> THE RAW REFERRER IS NEVER STORED, AND THAT IS A PROPERTY RATHER THAN A PROMISE. <<< It is an
// unbounded string a stranger's browser supplies, on a row this house keeps forever — the `events`
// table's own first law is *never free text*, and P2-4C's leak law binds on anything durable. It is
// resolved to a rostered KEY at the door and the string is dropped there.
//
// >>> UTM OUTRANKS THE REFERRER, AND THE ORDER IS LOAD-BEARING. <<< An ad platform TAGS its own
// links, and the referrer it arrives with is a redirector, an app webview, or nothing at all — so a
// referrer-first reader files her paid traffic as `direct` and the campaign reads as organic.
export function trafficSource({ utm = "", referrer = "" } = {}) {
  const tag = lower(utm);
  if (tag) {
    if (SOURCE_SET.has(tag)) return tag;
    const alias = UTM_ALIASES.get(tag);
    if (alias) return alias;
    // A TAG NOBODY ROSTERS IS `other` AND NEVER `direct`: she tagged the link, so somebody sent
    // this person, and only the roster is missing.
    return "other";
  }
  const host = referrerHost(referrer);
  if (!host) return "direct";
  for (const [suffix, source] of REFERRER_SOURCES) {
    if (host === suffix || host.endsWith(`.${suffix}`)) return source;
  }
  return "other";
}

// ---------------------------------------------------------------------------
// WHAT SITE TRAFFIC COUNTS.
//
// **Her own dashboard is excluded and nothing else is**, which is the exact mirror of the rooms
// tile's `UNTRACKED_PAGES` and is declared for the same reason: excluded-on-purpose and forgotten
// look identical until both sides are named. Her admin visits in her own visitor count would make
// the number she runs the business on partly a number about herself.
// ---------------------------------------------------------------------------
export const TRAFFIC_UNTRACKED = new Set(["dashboard"]);

// **THE FUNNEL IS COUNTED HERE. IT IS THE ONLY PAGE ON THIS SITE THAT EXISTS TO RECEIVE PAID
// TRAFFIC, AND IT IS THE ONE PAGE THE ROOMS TILE MUST NOT COUNT.** Held as a biconditional by the
// fixture against the server's own `TRACKED_PAGES` / `UNTRACKED_PAGES`, in both directions.
export const TRAFFIC_FUNNEL_PAGE = "funnel";

// ---------------------------------------------------------------------------
// SURFACE 2(b) — **WHICH DOOR THIS VISIT WAS, AND THE ANSWER IS THE PAGE'S OWN.**
//
// All three of her funnels are ONE page with a parameter, so the collector's `page` column can say
// *somebody landed on the funnel* and can never say *somebody saw the wealth ad* — which is the one
// number her report card was missing and the reason it printed **no submission rate at all**.
//
// >>> IT RESOLVES THROUGH `funnelFromParam`, WHICH IS WHAT MAKES THE DENOMINATOR AND THE NUMERATOR
// THE SAME DOOR. <<< An absent or unrostered `f` renders her FIRST door and files the lead under it
// (F1's ruling), so a visit filed under `null` — or under a raw parameter — would put submissions on
// one row and the people who saw that page on another. **The rate is then arithmetic between two
// different questions**, which is exactly the class she caught on the signups number ("42 in one
// place meaning something else elsewhere").
//
// **A PAGE THAT IS NOT THE FUNNEL HAS NO FUNNEL, AND THAT IS `null` RATHER THAN THE FALLBACK.** The
// parameter rides every ping in the session (it is read once at landing, like the referrer and the
// tag, because `goTo` rewrites the address), so resolving it unconditionally would file her whole
// site's traffic under `wealthy` — every room visit counted as an ad impression, inflating the one
// number she buys advertising against, silently and in the direction nobody questions (R8-4).
// ---------------------------------------------------------------------------
// >>> **HER `/talk` DOOR JOINS THE LEDGER (2026-08-27), AND IT IS A DOOR RATHER THAN A FUNNEL.** <<<
//
// Her item: *"the analytics auto-registration — the /talk funnel line live, every future funnel
// self-registering into analytics and the broadcast picker."* The line did not exist, and the reason
// it did not is worth keeping because it is also the reason it may not be fixed the obvious way.
//
// **`/talk` IS DELIBERATELY NOT ON `FUNNEL_KEYS` AND MUST NOT JOIN IT.** `src/talkDoor.js` states
// why: a key on that roster CAPTURES A LEAD AND MAILS A PAID READING, which her ITEM 2 signup-first
// flow does not want. So adding it there to get an analytics row would change what the door DOES in
// order to change what a chart SAYS — the tail wagging the product.
//
// **SO THERE ARE TWO ROSTERS AND THEY ANSWER TWO QUESTIONS (R18).** `FUNNEL_KEYS` is *which doors
// capture a lead and mail a reading*. `ANALYTICS_DOORS` is *which doors her advertising can land on*,
// which is the question her report card asks. The second is DERIVED from the first, so **a fourth
// funnel joins her analytics by EXISTING** — which is the self-registration half of her item, and the
// half that stops this needing a person to remember.
//
// **AND `/talk` IS ITS OWN DOOR KEY RATHER THAN A PARAMETER**, because unlike her three ad funnels it
// is a whole page rather than one page with an `f=`. The page IS the door, so the answer is the page.
export const TRAFFIC_TALK_PAGE = "talk";

// >>> **THE PAGE IS `talk` AND THE DOOR IS `ask-lala`, AND THEY ARE TWO FACTS (R18).** <<<
//
// **CAUGHT 2026-08-27, BEFORE THE ROW THAT WOULD HAVE HIDDEN IT WAS DRAWN.** The first cut of this
// file filed `/talk` visits under the PAGE name, and `users.landing_tag` has filed the same door
// under `ask-lala` since the day it was declared. **Measured on her live rows: 214 visits under one
// key and 203 accounts under the other** — the same door, two spellings, and her report card joins
// visits to signups BY THAT KEY. A row built on it would have shown a door with visitors and no
// members beside a door with members and no visitors, and both halves would have looked fine.
//
// The tag is the older, established spelling and the one 203 real accounts already carry, so the
// LEDGER moves to it rather than the other way round.
export const ANALYTICS_DOORS = Object.freeze([...FUNNEL_KEYS, TALK_TAG]);

// ================================================================================================
// >>> **WHAT EACH DOOR CAN ANSWER, WHICH IS NOT THE SAME FOR ALL OF THEM (2026-08-27).** <<<
//
// Her report card has eight columns and her `/talk` door can honestly fill four of them. That door
// **captures no lead and mails no reading**: there is no `funnel_leads` row behind it at all, which
// is `talkDoor.js`'s whole ruling and the reason `/talk` is deliberately off `FUNNEL_KEYS`. So
// *gave details*, *got a reading*, *left a phone* and *off the list* are not small numbers for that
// row — **they are questions nobody asked it.**
//
// **AND A ZERO WOULD BE THE WRONG ANSWER TO ALL FOUR (R43's M8: an absence and a zero look identical,
// and only one of them is an answer).** A `/talk` row printing `0` under *gave details* says the
// door is failing to convert, on a screen she uses to decide which advertisement to keep paying for —
// the one direction a wrong number here costs real money. It reads *not asked*, and the table says
// once, in her own words, why.
//
// It is a PURE law over the roster rather than a flag on each row, so the fixture runs it and a
// fourth door joins by being on `ANALYTICS_DOORS` (R11/R31).
export function doorCapturesLead(key) {
  return typeof key === "string" && FUNNEL_KEYS.includes(key);
}

/** The columns a door with no lead row cannot answer, named ONCE so the table and the fixture read
 *  the same list. Ordered as her columns are. */
export const LEAD_ONLY_COLUMNS = Object.freeze(["submissions", "readings", "withPhone", "unsubscribed"]);

/**
 * **HER OWN HEADLINE FOR THE DOOR, AND THE `/talk` DOOR HAS ONE NOW BECAUSE IT HAD A WRONG ONE.**
 *
 * `funnelCopy` resolves through `funnelFromParam`, which FALLS BACK to `wealthy` for anything it
 * does not recognise — so the moment `ask-lala` joined `ANALYTICS_DOORS`, asking it for a title
 * answered *"Were You Born to Be Wealthy?"*. Her `/talk` row would have carried another door's
 * advertisement as its name, on the screen whose only job is telling her doors apart, and it would
 * have looked like an ordinary row (R8-4: the silent direction).
 *
 * The three funnel titles are still `funnelCopy`'s and are NOT re-spelled here (R28/R29): they are a
 * contract with live advertising and this function holds no opinion about them.
 */
export function doorTitle(key) {
  if (key === TALK_TAG) return "Ask Lala";
  return doorCapturesLead(key) ? funnelCopy(key).title : "";
}

/**
 * Is this a door her analytics counts? **PRESENCE, NEVER TRUTHINESS (R53/R136/R137)** — this guards a
 * DURABLE COLUMN and the value arrives off a query string, so anything that is not exactly a rostered
 * key is nothing.
 */
export function doorRostered(key) {
  return typeof key === "string" && ANALYTICS_DOORS.includes(key);
}

// **A PAGE THAT IS NOT ONE OF HER DOORS HAS NO DOOR, AND THAT IS `null` RATHER THAN THE FALLBACK.**
// The parameter rides every ping in the session (it is read once at landing, like the referrer and
// the tag, because `goTo` rewrites the address), so resolving it unconditionally would file her whole
// site's traffic under `wealthy` — every room visit counted as an ad impression, inflating the one
// number she buys advertising against, silently and in the direction nobody questions (R8-4).
export function visitFunnel(page, raw) {
  if (page === TRAFFIC_FUNNEL_PAGE) return funnelFromParam(raw);
  // Her `/talk` door files under its own key with no parameter to read: the PAGE is the door, and
  // the door's name is the one her `landing_tag` already uses, never the page's.
  if (page === TRAFFIC_TALK_PAGE) return TALK_TAG;
  return null;
}

// ---------------------------------------------------------------------------
// **WHAT THE FUNNEL VISITS NUMBER MAY BE USED FOR IN A GIVEN WINDOW — THREE STATES, NEVER TWO
// (R18), AND THE MIDDLE ONE IS HER ACCURACY LAW.**
//
// *Every number traceable to its source, no estimates dressed as counts.* The collector records
// FORWARD ONLY (migration 108 back-fills nothing, and 109 back-fills nothing) — so a period that
// begins before it started counting has **real submissions divided by a denominator that does not
// cover them**. That rate is not merely wrong, it is wrong in the flattering direction: it can read
// well over 100%, and where it does not, it makes every funnel look like it converts better than it
// does. She buys advertising off this row.
//
//   `unmeasured` — nothing has ever been counted. F5's original sentence, unchanged.
//   `partial`    — counting began INSIDE this window. Visits are shown and **no rate is**, with the
//                  date named, which is R17/R160's own shape: print what is known, name what is not.
//   `measured`   — the window is fully covered and the rate is a real one.
//
// The boundary is `<=` rather than `<` on purpose: a collector whose first visitor arrived at the
// exact millisecond the window opened covers that window, and the honest reading of a tie is the one
// that does not withhold a rate she is entitled to.
export function visitCoverage({ collectingSince = null, rangeStartMs = 0 } = {}) {
  const since = collectingSince instanceof Date ? collectingSince.getTime() : Date.parse(String(collectingSince ?? ""));
  if (!Number.isFinite(since)) return "unmeasured";
  const start = Number(rangeStartMs);
  // A window with no readable start cannot be proved covered, and the safe direction here is the one
  // that withholds a rate rather than the one that publishes an unverifiable one (R19/R8-4).
  if (!Number.isFinite(start)) return "partial";
  return since <= start ? "measured" : "partial";
}

/**
 * >>> **WHAT A PERIOD BIGGER THAN THE DATA SAYS ON HER SCREEN (her report, 2026-08-14).** <<<
 *
 * *"Analytics site traffic shows THE SAME totals for Today, 7 days and 10 days."* Traced: the
 * collector holds ONE Pacific day of rows, so every window contains the same set and the tiles are
 * arithmetically honest. **Sameness with no explanation beside it reads as breakage**, which is a
 * dashboard costing her trust for telling the truth.
 *
 * **THE SENTENCE NAMES THE SAMENESS AND NOT ONLY THE DATE, WHICH IS THE WHOLE REPAIR.** *"Counted
 * since the 14th"* is a fact she has to do arithmetic on before it explains what she is looking at;
 * *"every period will show the same totals until there is more than one day"* is the answer to the
 * question she actually asked. It stops being drawn the moment the window is really covered, because
 * a screen that keeps apologising after the gap has closed is one she stops reading (SURFACE 2(b)'s
 * own ruling about `measured` saying nothing).
 *
 * **THREE STATES, NEVER TWO (R18).** `unmeasured` is *nothing has been counted at all* — a different
 * fact from *counted, but not this far back* — and collapsing them tells her a working collector has
 * a gap, or tells her a dead one is merely young.
 */
export function trafficCoverageNote(coverage, collectingSince = null) {
  if (coverage === "measured") return "";
  if (coverage === "unmeasured") return "Traffic counting has not recorded its first visitor yet, so these numbers are empty rather than low.";
  const day = String(collectingSince ?? "").slice(0, 10);
  const since = /^\d{4}-\d{2}-\d{2}$/.test(day) ? ` on ${day}` : "";
  return `This period reaches back before traffic counting began${since}, so it shows everything counted so far.`
    + " Every period will show the same totals until there is more than one day of history.";
}

// The rate she reads, or nothing. **`null` is not `0%`** — *nobody converted* and *nobody arrived*
// are different facts and only the first is a problem (F5's `ratePct` ruling, one surface over, and
// this is the same law asked of a denominator that may not be usable at all).
export function funnelSubmitRate(submissions, visitors, state) {
  if (state !== "measured") return null;
  const whole = count(visitors);
  if (whole <= 0) return null;
  return Math.round((count(submissions) / whole) * 100);
}

// A page is a member-supplied string. It is not rostered against the rooms list, because site
// traffic legitimately covers pages that list has never named — but it is BOUNDED, because the
// column is durable. Anything unusable is dropped rather than stored.
export function trafficCounts(page) {
  if (typeof page !== "string") return false;
  const key = page.trim();
  if (!key || key.length > 40) return false;
  if (TRAFFIC_UNTRACKED.has(key)) return false;
  // Never free text: a page is a slug from the client's own `PAGE_PATHS`, so it is letters, digits
  // and hyphens and nothing else.
  return /^[a-z0-9-]+$/.test(key);
}

// ---------------------------------------------------------------------------
// THE VISITOR ID.
//
// >>> IT IS MINTED BY THE SERVER AND THE CLIENT NEVER SEES IT. <<< The cookie is `HttpOnly`, so no
// script on the page — ours, an ad vendor's, or anybody else's — can read it, forge it or carry it
// off site. It carries NO member fact: it is randomness and nothing else, and it is the only thing
// that makes *how many visitors* and *new vs returning* answerable at all.
//
// **FAIL CLOSED ON ANYTHING THAT IS NOT ONE (R19/R8-4).** A visit recorded under a malformed id is a
// visitor invented out of nothing, and an invented visitor renders perfectly — it is a number she
// would make spending decisions on. A visit with no usable id is dropped, which costs one row and
// can never inflate a count.
// ---------------------------------------------------------------------------
export const TRAFFIC_COOKIE = "twelve_house_visitor";
export const VISITOR_ID_LENGTH = 24;

// PRESENCE, NEVER TRUTHINESS (R53/R136/R137): this value arrives off a cookie header, where
// everything is a string — `Boolean("false")` is `true`, and the coercion that once cast a whole
// chart at the equator is the same one that would count `true` as a visitor.
export function visitorIdValid(id) {
  return typeof id === "string" && id.length === VISITOR_ID_LENGTH && /^[a-z0-9]+$/.test(id);
}

// The randomness is a PARAMETER, so a gate RUNS the mint with no crypto and no server (R46's
// `doFetch`-is-a-parameter pattern). A source of bytes that cannot fill the id answers "" rather
// than a short one, because a short id is one `visitorIdValid` refuses and a refused mint is a
// visitor lost silently on every single request.
// >>> THE LENGTH CHECK THAT STOOD HERE WAS REMOVED BECAUSE IT COULD NOT CHANGE AN ANSWER, AND THIS
// ROUND'S OWN MUTATION RUN IS WHAT PROVED IT (R216's ruling, applied rather than re-derived). <<< A
// source of bytes too short to fill the id hands `undefined` at some index, and the per-byte guard
// below already refuses that — so `bytes.length < VISITOR_ID_LENGTH` was a line no mutation could
// reach and no reader could trust. What survives is the guard that really decides.
export function mintVisitorId(randomBytes) {
  const bytes = typeof randomBytes === "function" ? randomBytes(VISITOR_ID_LENGTH) : randomBytes;
  if (!bytes) return "";
  let out = "";
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < VISITOR_ID_LENGTH; i += 1) {
    // >>> A REAL NUMBER, NEVER A COERCED ONE (R136/R137), AND THE FIXTURE CAUGHT THE FIRST DRAFT
    // DOING IT. <<< `Number("7")` is 7 and `Number(true)` is 1, so a `Number(...)` here happily
    // builds a perfectly valid-looking id out of a source of bytes that is not one — and every
    // visit written under it is a visitor invented out of nothing.
    const n = bytes[i];
    if (typeof n !== "number" || !Number.isInteger(n) || n < 0) return "";
    out += alphabet[n % alphabet.length];
  }
  return out;
}

// ---------------------------------------------------------------------------
// THE ROW SHE READS.
//
// **`returning` IS DERIVED AND IS NEVER A SECOND COUNT (R28/R29).** Two counts of the same people
// resolved by two queries is two answers to one question, and the one that drifts is the one nobody
// checks. It is `visitors - new`, in ONE place, read by the surface.
//
// **AND IT IS FLOORED AT ZERO, WHICH IS NOT DEFENSIVENESS.** A visitor whose first sighting lands
// inside the window while her visit rows were written a moment either side of a period boundary can
// legitimately make `new` exceed `visitors` by one, and **she would read *-1 returning* on the top
// line of her own analytics** — ITEM 3's own floor, one surface over, for the same reason: it
// changes no verdict, it stops a number that reads as broken.
// ---------------------------------------------------------------------------
const count = (v) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0);

export function trafficLine({ visitors, views, newVisitors } = {}) {
  const total = count(visitors);
  // >>> THE CLAMP IS THE LOAD-BEARING HALF AND THE FLOOR UNDER `back` WAS NOT, WHICH THIS ROUND'S OWN
  // MUTATION RUN FOUND (R216). <<< `Math.min` already guarantees `total - fresh >= 0`, so a
  // `Math.max(0, ...)` below it was a line no mutation could reach. The clamp does two jobs at once:
  // it stops `returning` going negative AND it stops `new` itself rendering larger than the visitor
  // count it is a share of.
  const fresh = Math.min(count(newVisitors), total);
  const back = total - fresh;
  return {
    visitors: total,
    views: count(views),
    newVisitors: fresh,
    returning: back,
    // A PERCENTAGE OF NOTHING IS NOT ZERO, IT IS NOTHING (R18). A quiet period rendering "0% new"
    // reads as a fact about her audience; `null` is the surface's cue to print nothing at all.
    newPct: total ? Math.round((fresh / total) * 100) : null,
    returningPct: total ? Math.round((back / total) * 100) : null,
  };
}

// ---------------------------------------------------------------------------
// ITEM 9 / T3 — **SOURCE-LEVEL CONVERSION, AND THE TWO NUMBERS ON A ROW ARE ABOUT TWO DIFFERENT
// POPULATIONS.** Her clause: *visits carrying a source that end in signup show per-source signup
// rates, so she can see Instagram versus TV-era direct traffic versus ads at a glance — with HONEST
// LABELLING WHERE ATTRIBUTION IS UNKNOWABLE, a plain note, NOT FAKE PRECISION.*
//
// >>> `visitors` AND `arrivals` ARE NOT THE SAME PEOPLE, AND THE RATE MAY ONLY EVER BE DIVIDED BY
// THE SECOND. <<< `visitors` counts everybody with a VISIT carrying this source inside the window —
// which is the right answer to *what did this campaign bring me this month* and includes people who
// first arrived a year ago. `arrivals` counts people whose FIRST sighting anywhere on this site is
// this source AND is inside the window, which is the only population a signup can be attributed to.
// **Dividing signups by `visitors` mixes two populations, is routinely wrong, and can exceed 100%**
// — which is exactly the fake precision her clause forbids, arriving as a number she would spend
// against. It is the version anybody would write, because both figures are sitting on the same row.
//
// **IT IS A COHORT RATE AND BOTH HALVES ARE INSIDE THE WINDOW**, which is SURFACE 2(c)'s own ruling
// on `paid` one table down: *of the people who arrived in this period, how many joined in it.* A
// visitor who arrives today and joins next month is not counted here and is not a defect — she is a
// different question, and answering her would need a figure that moves after the fact.
//
// **A SOURCE WITH ARRIVALS AND NO VISITS IS STILL DRAWN** (R43's M8, and SURFACE 2(b)'s own
// joined-by-door shape): the two halves come off two queries with two keys, and a row dropped
// because one of them missed it is a source converting invisibly. The two are UNIONED, never
// intersected.
export function trafficSourceRows(rows, conversions = []) {
  const byKey = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = row && typeof row.source === "string" ? row.source : "";
    if (!trafficSourceValid(key)) continue;
    byKey.set(key, (byKey.get(key) || 0) + count(row.visitors));
  }
  // The conversion half is rostered by the SAME test, so a key from anywhere else cannot reach her
  // table through the second door either.
  const arrivalsBy = new Map();
  const doorBy = new Map();
  const signupsBy = new Map();
  const paidBy = new Map();
  for (const row of Array.isArray(conversions) ? conversions : []) {
    const key = row && typeof row.source === "string" ? row.source : "";
    if (!trafficSourceValid(key)) continue;
    arrivalsBy.set(key, (arrivalsBy.get(key) || 0) + count(row.arrivals));
    doorBy.set(key, (doorBy.get(key) || 0) + count(row.reachedDoor));
    signupsBy.set(key, (signupsBy.get(key) || 0) + count(row.signups));
    paidBy.set(key, (paidBy.get(key) || 0) + count(row.paid));
  }
  const total = [...byKey.values()].reduce((a, b) => a + b, 0);
  const keys = new Set([...byKey.keys(), ...arrivalsBy.keys()]);
  return [...keys]
    .map((source) => {
      const visitors = byKey.get(source) || 0;
      const arrivals = arrivalsBy.get(source) || 0;
      // A signup cannot exceed the cohort it came from. The clamp is reachable — these numbers
      // arrive off a payload — and without it a malformed read prints "300% joined" on the one
      // table she buys advertising against.
      const signups = Math.min(signupsBy.get(source) || 0, arrivals);
      // >>> A5 — EVERY RUNG IS CLAMPED TO THE ONE ABOVE IT, AND EACH CLAMP IS REACHABLE. <<< These
      // numbers arrive off a payload and come from four separate `FILTER` clauses, so nothing in SQL
      // makes them a ladder; without the clamps a malformed read prints *120% reached a door* or
      // *more paid than joined* on the one table she buys advertising against (the `signups` clamp
      // directly above is this house's own precedent, and it was written for the same reason).
      const reachedDoor = Math.min(doorBy.get(source) || 0, arrivals);
      const paid = Math.min(paidBy.get(source) || 0, signups);
      return {
        source, visitors, arrivals, reachedDoor, signups, paid,
        pct: total ? Math.round((visitors / total) * 100) : null,
        // >>> THE RUNG THAT SEPARATES *THIS CHANNEL IS BAD* FROM *THIS CHANNEL'S LINKS ARE POINTING
        // AT THE WRONG PAGE.* <<< Measured on her own rows for 2026-08-22: TikTok brought 68% of the
        // day and TWO THIRDS of it never reached a funnel door at all. A percentage of nothing is
        // nothing rather than zero (R18), exactly as the signup rate beside it.
        doorPct: arrivals ? Math.round((reachedDoor / arrivals) * 100) : null,
        // Of the people who JOINED from this source, how many are paying. The denominator is the
        // signups rather than the arrivals on purpose: *how well does this channel sell* and *how
        // well does it bring people* are two questions (R18), and the second is `signupPct`.
        paidPct: signups ? Math.round((paid / signups) * 100) : null,
        // A PERCENTAGE OF NOTHING IS NOTHING, NOT ZERO (R18). Nobody new arrived from this source in
        // this window, so there is no cohort to rate — and "0%" there reads as a source that brings
        // people who never join, which is a fact about her advertising rather than about the period.
        signupPct: arrivals ? Math.round((signups / arrivals) * 100) : null,
      };
    })
    .filter((r) => r.visitors > 0 || r.arrivals > 0)
    .sort((a, b) => b.visitors - a.visitors || TRAFFIC_SOURCES.indexOf(a.source) - TRAFFIC_SOURCES.indexOf(b.source));
}

// >>> HER *HONEST LABELLING WHERE ATTRIBUTION IS UNKNOWABLE* CLAUSE, AS ONE PLAIN SENTENCE. <<< A
// television spot, a word of mouth and an app that strips the referring link all arrive carrying
// nothing, and `trafficSource` files all three as `direct` — correctly, since nobody told this house
// where they came from. So **Direct is not the same as organic**, and while a TV flight is running it
// is where most of that money lands. Saying so is what stops the biggest row on her table being read
// as an audience she already had.
export const TRAFFIC_ATTRIBUTION_NOTE =
  "Joined counts people who first arrived from that source in this window and made an account in it. "
  + "Anything that arrives without a tag — a television spot, word of mouth, an app that strips the link — "
  + "lands in Direct, so Direct is not the same as an audience you already had.";

// What she reads each source called. Never the key — S1-A/R117's law binds on any member-facing or
// owner-facing line, and `trustedtv` is machinery.
export const TRAFFIC_SOURCE_LABELS = {
  direct: "Direct",
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  youtube: "YouTube",
  pinterest: "Pinterest",
  x: "X",
  google: "Search",
  email: "Email",
  vibe: "Vibe TV",
  roku: "Roku",
  trustedtv: "Trusted TV",
  // CADENCE-1 (2026-08-21): what she calls the never-paid cliffhanger cadence on her own
  // screens. Never the key — `cadence` is machinery and S1-A/R117 binds on any owner-facing
  // line, which is exactly what the gate that caught this absence exists to say.
  cadence: "The cliffhanger emails",
  other: "Somewhere else",
};

// An unrostered key can never reach this, because `trafficSourceRows` refuses one — but the label
// table is asked by NAME so a source added to the roster without a label goes RED in the gate rather
// than putting a machine key on her screen.
export const trafficSourceLabel = (key) => TRAFFIC_SOURCE_LABELS[key] || "";

// =================================================================================================
// >>> HER TRAFFIC SPLIT (2026-08-18): TOTAL STAYS THE HEADLINE, AND TWO BOXES UNDER IT SEPARATE THE
// STRANGERS FROM THE MEMBERS. <<<
//
// Her order: *"1. TOTAL SITE TRAFFIC stays as the headline. 2. THE NEW BOX: NON-MEMBER TRAFFIC —
// visitors with NO signed-in session and no member cookie, the strangers, counted separately:
// visitors, page views, new-versus-returning within that class, and WHERE FROM (the sources ranked
// for strangers specifically — THIS IS THE ADS' REPORT CARD). 3. THE COMPANION BOX: MEMBER TRAFFIC,
// so the two halves visibly sum toward the total. 4. THE HONEST EDGE, NAMED ON THE SURFACE: a member
// browsing logged-OUT counts as a stranger (the web cannot know better)."*
//
// **THE DISCRIMINATOR WAS ALREADY ON THE ROW AND NEEDED NO MIGRATION (R25/R180):** `site_visits`
// has carried `user_id` since migration 108, stamped from the session at the door. A visit with one
// is a signed-in visit and a visit without one is a stranger's — which is her clause 2 and her
// clause 4 at once, because a member browsing logged out really does arrive with no session and
// **the web cannot know better.** That is not a shortcoming of this reading, it is the fact her own
// caveat names.
//
// **THE CLASSES ARE A PARTITION OF THE VISITS, HELD AS ONE**, so a third class cannot appear and a
// visit cannot fall through both.
// =================================================================================================
export const VISIT_CLASSES = Object.freeze(["stranger", "member"]);

export const VISIT_CLASS_LABELS = Object.freeze({
  stranger: "Non-member traffic",
  member: "Member traffic",
});
export const visitClassLabel = (key) => VISIT_CLASS_LABELS[key] || "";

/**
 * >>> HER CLAUSE 3's *VISIBLY SUM* IS TRUE OF PAGE VIEWS AND IS **NOT** TRUE OF VISITORS, AND SAYING
 * SO IS THE ROUND'S ONE HARD PIECE OF HONESTY. <<<
 *
 * Every visit ROW belongs to exactly one class, so the two page-view counts add to the total
 * exactly. **A VISITOR does not**: one browser that reads a room signed out and then signs in has a
 * visit in each class, so she is counted in BOTH — the two visitor counts sum to the total or to
 * MORE, never to less. Printed as a fact rather than papered over, because a screen that claims two
 * numbers add up when they do not is the kind of thing she checks with a calculator once and never
 * trusts again (R141).
 */
export function trafficClassSum(traffic) {
  const cls = traffic && typeof traffic === "object" ? traffic.classes : null;
  if (!cls || typeof cls !== "object") return null;
  const num = (v) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null);
  const total = num(traffic.views);
  const parts = VISIT_CLASSES.map((k) => num(cls[k]?.views));
  if (total === null || parts.some((v) => v === null)) return null;
  const sum = parts.reduce((a, b) => a + b, 0);
  return { views: sum, total, exact: sum === total };
}

/**
 * The line for ONE class, derived through `trafficLine` rather than beside it — so `returning`, the
 * floor under it and the withheld percentages are the SAME arithmetic the headline uses, and a
 * change to that law moves all three boxes together (R28/R29).
 *
 * **A CLASS THE PAYLOAD DOES NOT CARRY IS `null` AND NOT A ROW OF ZEROS (R18/R65):** a browser
 * holding a bundle older than this round, or a read that refused, is *we do not know* — and drawing
 * three tiles of `0` under **Non-member traffic** says her advertising brought nobody, which is a
 * claim about her spend rather than about a payload.
 */
export function trafficClassLine(traffic, cls) {
  return trafficClassRow(traffic, cls) ? trafficLine(trafficClassRow(traffic, cls)) : null;
}

/** Her clause 2: the sources ranked FOR STRANGERS SPECIFICALLY — the ads' report card. */
export function trafficClassSources(traffic, cls) {
  const row = trafficClassRow(traffic, cls);
  return row ? trafficSourceRows(row.sources) : [];
}

/**
 * >>> THE ROSTER GUARD THAT STOOD HERE CAME OUT (R216), AND THE MUTATION RUN IS WHAT FOUND IT. <<<
 * `VISIT_CLASSES.includes(cls)` could not change an answer: an unrostered key reads `undefined` off
 * the payload and every inherited name on `Object.prototype` is a FUNCTION, so the shape test below
 * already refuses all of them. A line no mutation can reach is a line no reader can trust. The
 * partition is still declared -- `VISIT_CLASSES` is what the surface maps and what the store's own
 * `VISIT_CLASS_SQL` is held against -- and it is now the ONLY place it is asked, rather than a
 * second, dead copy of the question.
 */
function trafficClassRow(traffic, cls) {
  const row = traffic && typeof traffic === "object" && traffic.classes ? traffic.classes[cls] : null;
  return row && typeof row === "object" ? row : null;
}

/**
 * >>> HER CLAUSE 4's SECOND HALF: THE FUNNEL LINE READS FROM THE NON-MEMBER CLASS BY DEFAULT. <<<
 * *"conversion math against total traffic understates how well the ads actually convert."* Her
 * 636-and-twelve recomputed against true stranger traffic is exactly this number.
 *
 * **IT FALLS BACK TO THE TOTAL RATHER THAN TO NOTHING**, and the direction is the ruling (R19): a
 * payload with no split in it is an older read, and answering `null` there would blank the
 * conversion ladder on her dashboard over a field that has nothing to do with it. The fallback is
 * exactly what that row showed before this round, so the failure mode is the status quo.
 */
export function funnelVisitorBase(traffic) {
  const line = trafficClassLine(traffic, "stranger");
  if (line) return { visitors: line.visitors, returning: line.returning, basis: "stranger" };
  const whole = traffic && typeof traffic === "object" ? trafficLine(traffic) : null;
  return whole ? { visitors: whole.visitors, returning: whole.returning, basis: "all" } : null;
}

/**
 * Her clause 4's caveat, in one plain line. **It names the LIMIT rather than the mechanism** (S1-A/
 * R117: no machinery on a surface), and it is a DECLARATION rather than a literal in the markup, so
 * the gate can read it and there is one place it is worded.
 */
export const TRAFFIC_CLASS_NOTE =
  "A member browsing while signed out is counted as a non-member here, because a signed-out visit "
  + "looks the same from the outside. One browser that does both is counted in each box, so the "
  + "visitor counts can add up to a little more than the total; the page views add up exactly.";
